// UCC tab — filings table. The lapse countdown is the loudest column
// (error token when past/≤30 days, warning inside the continuation
// window); each row carries a source chip — "Baselayer" (info token,
// deep-links to the Bgc filing workspace) vs muted "Manual".

import { Link } from "react-router-dom";
import { Table, THead, TBody, TR, TH, TD, Badge } from "@/design-system/components/ui";
import { calendarDate, daysLabel, daysUntil } from "../common/colorBand";
import { lapseSeverity, normalizeUccStatus } from "./lapse";
import type { LapseSeverity } from "./lapse";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";

interface ActiveFilingsTableProps {
  filings: UccFilingDto[];
  onRowClick: (filing: UccFilingDto) => void;
}

const SEVERITY_TEXT_CLASS: Record<LapseSeverity, string> = {
  error: "text-status-error",
  warning: "text-status-warning",
  none: "text-onyx",
};

function statusBadge(f: UccFilingDto) {
  // Backend statuses are lowercase ("active"/"continued"/"terminated"/
  // "lapsed") — normalize before comparing, title-case for display.
  const status = normalizeUccStatus(f.currentStatus);
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
  if (status === "lapsed" || status === "expired") {
    return <Badge status="error">{label}</Badge>;
  }
  if (status === "terminated") {
    return <Badge status="archived">Terminated</Badge>;
  }
  if (status === "continued") {
    return <Badge status="review">Continued</Badge>;
  }
  return <Badge status="finalized">{label}</Badge>;
}

/** "Baselayer" info chip deep-linking to the Bgc workspace, or muted
 *  "Manual" for servicing-entered rows. Exported for the drawer. */
export function FilingSourceChip({ filing }: { filing: UccFilingDto }) {
  if (filing.bgcUccFilingId) {
    return (
      <Link
        to={`/background-checks/ucc-filings/${filing.bgcUccFilingId}`}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Open Baselayer filing workspace for ${filing.filingNumber}`}
        data-testid={`ucc-source-baselayer-${filing.id}`}
      >
        <Badge status="info">Baselayer</Badge>
      </Link>
    );
  }
  return (
    <span
      className="text-caption text-gray-mid"
      data-testid={`ucc-source-manual-${filing.id}`}
    >
      Manual
    </span>
  );
}

export function ActiveFilingsTable({
  filings,
  onRowClick,
}: ActiveFilingsTableProps) {
  // ONE zone for BOTH halves of the lapse cell. `severity` tints the text and
  // `days` writes it; they are two reads of the same countdown, so a `tz` on
  // one and not the other is a cell whose colour contradicts its own label.
  return (
    <Table data-testid="ucc-filings-table">
      <THead>
        <TR>
          <TH className="text-white-pure">Filing #</TH>
          <TH className="text-white-pure">Type</TH>
          <TH className="text-white-pure">Jurisdiction</TH>
          <TH className="text-white-pure">Filed</TH>
          <TH className="text-white-pure">Days to lapse</TH>
          <TH className="text-white-pure">Status</TH>
          <TH className="text-white-pure">Source</TH>
        </TR>
      </THead>
      <TBody>
        {filings.map((f) => {
          const severity = lapseSeverity(f);
          const days = daysUntil(f.lapseDate);
          return (
            <TR
              key={f.id}
              onClick={() => onRowClick(f)}
              className="cursor-pointer"
              data-testid={`ucc-row-${f.id}`}
            >
              <TD className="font-mono text-caption">{f.filingNumber}</TD>
              <TD>{f.filingType}</TD>
              <TD>{f.filingJurisdiction}</TD>
              <TD>{calendarDate(f.filingDate)}</TD>
              <TD>
                <span
                  className={`font-display font-bold ${SEVERITY_TEXT_CLASS[severity]}`}
                  data-testid={`ucc-lapse-${f.id}`}
                  data-severity={severity}
                >
                  {daysLabel(days)}
                </span>
                <span className="block text-caption text-gray-mid">
                  {calendarDate(f.lapseDate)}
                </span>
              </TD>
              <TD>{statusBadge(f)}</TD>
              <TD>
                <FilingSourceChip filing={f} />
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
